/**
 * Campaign Incentives Service
 * 
 * Manages campaign incentives for marketing promotions.
 * Provides CRUD operations and specialized queries for incentive management.
 */
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { FirestorePathsService } from './firestorePaths.service';
import { FirestoreService } from './firestore.service';
import type { TCampaignIncentive, TQueryOptions } from '@shared/models';

@Injectable({
    'providedIn': 'root'
})
export class CampaignIncentivesService {
    constructor(
        private firestoreService: FirestoreService, 
        private firestorePathsService: FirestorePathsService
    ) {}

    public getNullable(): TCampaignIncentive {
        const incentive: TCampaignIncentive = {
            title: '',
            uid: ''
        };
        return incentive;
    }

    public getObservable(): Observable<TCampaignIncentive[]> {
        const path = this.firestorePathsService.getCampaignIncentivesPath();
        const observable = this.firestoreService.getObservable(path);
        return observable as Observable<TCampaignIncentive[]>;
    }

    public async getByUid(uid: string): Promise<any> {
        const path = this.firestorePathsService.getCampaignIncentivesPath();
        const response = await this.firestoreService.getByUid(path, uid);
        return response;
    }

    public async getAll(): Promise<TCampaignIncentive[]> {
        const path = this.firestorePathsService.getCampaignIncentivesPath();
        const incentives = await this.firestoreService.getAll(path) as TCampaignIncentive[];
        const sortedIncentives = this.sort(incentives);
        return sortedIncentives;
    }

    public async add(incentive: TCampaignIncentive): Promise<TCampaignIncentive> {
        const collectionPath = this.firestorePathsService.getCampaignIncentivesPath();
        const response = await this.firestoreService.add(collectionPath, incentive);
        return response as TCampaignIncentive;
    }

    public async update(incentive: TCampaignIncentive): Promise<TCampaignIncentive> {
        const collectionPath = this.firestorePathsService.getCampaignIncentivesPath();
        const response = await this.firestoreService.update(collectionPath, incentive);
        return response as TCampaignIncentive;
    }

    public async delete(uid: string): Promise<void> {
        const collectionPath = this.firestorePathsService.getCampaignIncentivesPath();
        return this.firestoreService.delete(collectionPath, uid);
    }

    public sort(incentiveSets: TCampaignIncentive[]): TCampaignIncentive[] {
        const sortedIncentives = incentiveSets.sort((a, b) => {
            if (a.title < b.title) {
                return -1;
            } else if (a.title > b.title) {
                return 1;
            } else {
                return 0;
            }
        });
        return sortedIncentives;
    }

    public async getByAccountNumber(accountNumber: string): Promise<TCampaignIncentive[]> {
        const collectionPath = this.firestorePathsService.getCampaignIncentivesColByAccountNumber(accountNumber);
        const response = await this.firestoreService.getAll(collectionPath);
        return response as TCampaignIncentive[];
    }

    public async getNextSequenceNumber(): Promise<number> {
        const path = this.firestorePathsService.getCampaignIncentivesPath();
        
        const queryOptions: TQueryOptions = {
            collectionPath: path,
            orderByField: 'sequenceNumber',
            orderByDirection: 'desc',
            limitCount: 1
        };
        
        const results = await this.firestoreService.queryCollection(queryOptions);
        
        let largestSequenceNumber = 0;
        if (results.length > 0) {
            const incentive = results[0] as TCampaignIncentive;
            if (incentive.sequenceNumber !== undefined) {
                largestSequenceNumber = incentive.sequenceNumber;
            }
        }
        
        const nextSequenceNumber = largestSequenceNumber + 1;
        return nextSequenceNumber;
    }

    // Commented method converted to modular API for future use
    // public async getByDateRange(mmStartingDate: Date, mmEndingDate: Date): Promise<TCampaign[]> {
    //     const startingDate = mmStartingDate.valueOf();
    //     const endingDate = mmEndingDate.valueOf();
    //
    //     const collectionPath = this.firestorePathsService.getCampaignsPath();
    //     const collectionRef = collection(this.firestore, collectionPath);
    //     
    //     const q = query(
    //         collectionRef,
    //         orderBy('sentDate'),
    //         startAt(startingDate),
    //         endAt(endingDate)
    //     );
    //     
    //     const snapshot = await getDocs(q);
    //     
    //     const campaigns: TCampaign[] = [];
    //     snapshot.forEach((doc) => {
    //         const campaign = doc.data() as TCampaign;
    //         campaigns.push(campaign);
    //     });
    //     
    //     return campaigns;
    // }
}